import {
  FFLNumberAllowedREGEX,
  FFLNumberBoxLengths,
  FFLNumberCleanREGEX,
  FFLNumberPlaceholders,
} from 'helpers/ValidationHelper';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';

export type FFLInputHandle = {
  focus: () => void;
};
type Props = {
  value?: string;
  onChange?: (v: string) => void;
  className?: string;
};

const FFLNumberInput = forwardRef<FFLInputHandle, Props>(
  ({ value = '', onChange, className }, ref) => {
    const inputsRef = useRef<(HTMLInputElement | null)[]>(
      new Array(FFLNumberBoxLengths.length).fill(null)
    );
    const splitValue = (inputValue: string) => {
      const clean = inputValue.replace(FFLNumberCleanREGEX, '').toUpperCase();
      const result: string[] = [];
      let index = 0;

      FFLNumberBoxLengths.forEach((len) => {
        result.push(clean.slice(index, index + len));
        index += len;
      });

      return result;
    };

    const [values, setValues] = useState<string[]>(() => splitValue(value));
    const normalizedValue = value.replace(FFLNumberCleanREGEX, '').toUpperCase();
    const renderedValues = values.join('') === normalizedValue ? values : splitValue(value);

    useImperativeHandle(ref, () => ({
      focus: () => inputsRef.current[0]?.focus(),
    }));

    const updateValue = (arr: string[]) => {
      setValues(arr);
      onChange?.(arr.join('').toUpperCase());
    };

    const handleChange = (i: number, val: string) => {
      val = val.toUpperCase();
      if (!FFLNumberAllowedREGEX.test(val)) return;

      const newValues = [...renderedValues];
      newValues[i] = val.slice(0, FFLNumberBoxLengths[i]);

      updateValue(newValues);

      // Move to next field
      if (val.length === FFLNumberBoxLengths[i] && i < FFLNumberBoxLengths.length - 1) {
        inputsRef.current[i + 1]?.focus();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent, i: number) => {
      if (e.key === 'Backspace' && !renderedValues[i] && i > 0) {
        inputsRef.current[i - 1]?.focus();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      const pasted = e.clipboardData.getData('Text').replace(FFLNumberCleanREGEX, '').toUpperCase();

      if (!pasted) return;

      e.preventDefault();

      let index = 0;
      const newValues = FFLNumberBoxLengths.map((len) => {
        const part = pasted.slice(index, index + len);
        index += len;
        return part;
      });

      updateValue(newValues);

      const revIndex = [...newValues].reverse().findIndex((v) => v.length > 0);

      const actualIndex = revIndex === -1 ? 0 : newValues.length - 1 - revIndex;

      inputsRef.current[actualIndex]?.focus();
    };

    return (
      <div
        className={`d-flex align-items-center w-100 ffl-number-wrapper`}
        role="group"
        aria-label="FFL Number"
        style={{ gap: 8 }}
      >
        {FFLNumberBoxLengths.map((len, i) => (
          <React.Fragment key={i}>
            <input
              ref={(el) => {
                inputsRef.current[i] = el;
              }}
              type="text"
              inputMode="text"
              pattern="[A-Za-z0-9]*"
              value={renderedValues[i] || ''}
              placeholder={FFLNumberPlaceholders[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={handlePaste}
              maxLength={len}
              aria-label={`FFL part ${i + 1}`}
              className={`form-control text-center ${className ?? ''}`}
              style={{
                flex: `${len} 1 0`,
                minWidth: 0,
              }}
            />

            {i !== FFLNumberBoxLengths.length - 1 && (
              <span className="fw-bold text-muted dash-text">-</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }
);

export default FFLNumberInput;
